import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import { SystemTable } from '@sharedCR/common/SystemTable';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { TabsCDS } from '@sharedCR/coreDesignSystem/TabsCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { PAGE_CODES } from 'constants/pageCodes';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import useSort from 'hooks/useSort';
import useTableFilter from 'hooks/useTableFilter';
import { getConditionalArray } from 'utils/data';

import {
  getMasterPermissionsToPage,
  selectIsBrokerOrSubCompanyUser,
  selectIsClaimAllowed,
} from 'store/auth/selectors';
import { getLoanSpecificForms } from 'store/forms/actions';
import { getRolesAction, openDialogAction } from 'store/global/actions';
import {
  getApplicationIncomesAction,
  getLoanAssetsAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getPropertiesAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import {
  selectAssetsWithTypeAndAmount,
  selectConditionsList,
  selectExtendedBorrowers,
  selectIncomesWithTypeAndAmount,
  selectIsGetConditionsPendingStatus,
  selectIsLoanCanceled,
  selectPropertyValues,
  selectTasks,
} from 'store/loanApplications/selectors';
import {
  getLoanRolesAction,
  getTaskCategoriesAction,
  getTaskTypeDynamicFieldAction,
  getTaskTypesAction,
} from 'store/system/actions';
import { selectTaskTypes } from 'store/system/selectors';
import {
  getConditionCategoriesAction,
  getConditionsAction,
  getConditionSetsAction,
  getPriorToTypesAction,
  getSlaGroupsAction,
} from 'store/underwriting/actions';
import {
  selectConditionCategories,
  selectConditionCategoryOptions,
  selectPriorToAllOptions,
} from 'store/underwriting/selectors';
import { getUsersAction } from 'store/users/actions';

import useConditionDrilldown from '../hooks/useConditionDrilldown';

import AddBrokerConditionForm from './AddBrokerConditionForm';
import AddConditionsForm from './AddConditionsForm';
import { getConditionsTableConfig } from './Conditions.config';
import {
  ALL_TAB_VALUE,
  defaultOrder,
  OPEN_CONDITION_STATUSES,
  TableCellId,
} from './Conditions.constants';
import { conditionsTableStyles } from './Conditions.styles';
import { ConditionsTestIds } from './Conditions.testIds';
import {
  filterConditionsByTab,
  getConditionFilterOptions,
  getConditionPriorToName,
  getConditionStatus,
  getRelatedTasks,
  getTabsConfig,
  hasDeletedConditions,
  orderConditions,
  prepareConditionRow,
  resolveActiveTabValue,
} from './Conditions.utils';
import ConditionTasksTable from './ConditionTasksTable';

const Conditions = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const isBrokerOrSubCompanyUser = useSelector(selectIsBrokerOrSubCompanyUser);

  const { columnFilters, onColumnFiltersChange, applyFilters } = useTableFilter();

  const conditions = useSelector(selectConditionsList);

  const loanConditionStatuses = useMemo(
    () => conditions.map(condition => getConditionStatus(condition)),
    [conditions],
  );

  const { drilldown } = useConditionDrilldown({
    onColumnFiltersChange,
    statusColumnKey: isBrokerOrSubCompanyUser ? null : TableCellId.STATUS,
    openStatusValues: OPEN_CONDITION_STATUSES,
    availableStatuses: loanConditionStatuses,
  });

  const [activeTab, setActiveTab] = useState(() => drilldown?.priorToType ?? ALL_TAB_VALUE);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [addModalState, setAddModalState] = useState({ isOpen: false, initialForm: null, key: 0 });
  const { order, onRequestSort } = useSort(defaultOrder);

  const onToggleExpand = useCallback(rowId => {
    setExpandedRowId(prev => (prev === rowId ? null : rowId));
  }, []);

  const { isMobile } = useBreakpoints();

  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isGetConditionsPending = useSelector(selectIsGetConditionsPendingStatus);
  const tasks = useSelector(selectTasks);
  const taskTypesMap = useSelector(selectTaskTypes);
  const borrowers = useSelector(selectExtendedBorrowers);
  const incomes = useSelector(selectIncomesWithTypeAndAmount);
  const assets = useSelector(selectAssetsWithTypeAndAmount);
  const properties = useSelector(selectPropertyValues);
  const categories = useSelector(selectConditionCategories);
  const categoryOptions = useSelector(selectConditionCategoryOptions);
  const priorToOptions = useSelector(selectPriorToAllOptions);

  const { isManageAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_CONDITIONS),
  );
  const isSignOffAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_MANUALLY_SIGN_OFF),
  );
  const isUnsignOffAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_UNSIGN_OFF),
  );
  const isRejectAllowed = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_REJECT));
  const isOwnRejectAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_OWN_REJECT),
  );
  const isAdhocCreateAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_ADHOC_CREATE),
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    const conditionFetches = isBrokerOrSubCompanyUser
      ? [dispatch(getLoanBrokerConditionByApplicationIdAction({ applicationId }))]
      : [
          dispatch(getConditionsAction()),
          dispatch(getConditionSetsAction()),
          ...getConditionalArray(loanId, () => [
            dispatch(
              getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
            ),
          ]),
        ];

    const loanFetches = getConditionalArray(loanId, () => [
      dispatch(getLoanApplicationTasksAction()),
      dispatch(getLoanSpecificForms({ loanId, applicationId })),
    ]);

    Promise.all([
      ...conditionFetches,
      ...loanFetches,
      dispatch(getTaskCategoriesAction()),
      dispatch(getTaskTypesAction()),
      dispatch(getTaskTypeDynamicFieldAction()),
      dispatch(getApplicationIncomesAction({ applicationId })),
      dispatch(getLoanAssetsAction({ applicationId })),
      // Property entity grouping is a lender/employee view only — the broker/sub-company path
      // degrades to a single ungrouped bucket (no property headings), so skip the properties
      // fetch for brokers to avoid an employee-gated 403 (the DEV-5204/DEV-5444 bug class).
      ...getConditionalArray(!isBrokerOrSubCompanyUser, () => [
        dispatch(getPropertiesAction({ applicationId })),
      ]),
      // DEV-5444: condition categories back the list category filter, the Add-picker filter, and the
      // category chip names on BOTH the underwriter and partner views. The read is now TPO-readable
      // (Underwriting [AllowForRolesFilter(Tpo)]), so broker/sub-company sessions fetch it too — the
      // Category column filter offers them and CategoryCell resolves the id-only partner payload.
      dispatch(getConditionCategoriesAction()),
      dispatch(getSlaGroupsAction()),
      dispatch(getUsersAction({ includeDeleted: true })),
      dispatch(getLoanRolesAction({ includeDeletedRoles: true })),
      dispatch(getRolesAction({ includeDeletedRoles: true })),
      dispatch(getPriorToTypesAction()),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, loanId, setIsLoading, applicationId, isBrokerOrSubCompanyUser]);

  const onTabChange = useCallback((_event, value) => {
    setActiveTab(value);
    setExpandedRowId(null);
  }, []);

  const onCloseAddModal = useCallback(() => {
    setAddModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const customOnAssignFormClick = useCallback(newForm => {
    setAddModalState(prev => ({ isOpen: true, initialForm: newForm, key: prev.key + 1 }));
  }, []);

  const customOnAssignBrokerFormClick = useCallback(
    newForm => {
      dispatch(
        openDialogAction({
          header: 'Add conditions and condition sets',
          body: dialogProps => (
            <AddBrokerConditionForm
              isBrokerCompany
              formName={FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS}
              initialForm={newForm}
              loanId={loanId}
              {...dialogProps}
            />
          ),
          size: ModalSize.XL,
        }),
      );
    },
    [dispatch, loanId],
  );

  const onAddConditions = useCallback(() => {
    if (isBrokerOrSubCompanyUser) {
      dispatch(
        openDialogAction({
          header: 'Add condition',
          body: dialogProps => (
            <AddBrokerConditionForm
              isBrokerCompany
              customOnAssignFormClick={customOnAssignBrokerFormClick}
              formName={FormNames.ADD_CONDITION}
              {...dialogProps}
            />
          ),
          size: ModalSize.XL,
        }),
      );

      return;
    }

    setAddModalState(prev => ({ isOpen: true, initialForm: null, key: prev.key + 1 }));
  }, [customOnAssignBrokerFormClick, dispatch, isBrokerOrSubCompanyUser]);

  const categoryNameById = useMemo(
    () => Object.fromEntries(categories.map(({ id, name }) => [id, name])),
    [categories],
  );

  const entityIndex = useMemo(
    () => ({
      borrowers: Object.fromEntries((borrowers ?? []).map(entity => [entity.borrowerId, entity])),
      incomes: Object.fromEntries((incomes ?? []).map(entity => [entity.incomeId, entity])),
      assets: Object.fromEntries((assets ?? []).map(entity => [entity.assetId, entity])),
      properties: Object.fromEntries((properties ?? []).map(entity => [entity.propertyId, entity])),
    }),
    [borrowers, incomes, assets, properties],
  );

  const tabs = useMemo(
    () => getTabsConfig({ conditions, priorToOptions }),
    [conditions, priorToOptions],
  );

  const activeTabValue = resolveActiveTabValue({ tabs, activeTab, priorToOptions });

  const preparedRows = useMemo(
    () =>
      filterConditionsByTab({ conditions, activeTab: activeTabValue, priorToOptions }).map(
        condition =>
          prepareConditionRow({
            condition,
            priorToName: getConditionPriorToName({ condition, priorToOptions }),
            isBrokerCompany: isBrokerOrSubCompanyUser,
            tasks,
            taskTypesMap,
            categoryNameById,
          }),
      ),
    [
      conditions,
      activeTabValue,
      priorToOptions,
      isBrokerOrSubCompanyUser,
      tasks,
      taskTypesMap,
      categoryNameById,
    ],
  );

  const filterOptions = useMemo(
    () =>
      getConditionFilterOptions({
        conditions,
        categoryOptions,
        categoryNameById,
        selectedStatuses: columnFilters?.[TableCellId.STATUS]?.values,
      }),
    [conditions, categoryOptions, categoryNameById, columnFilters],
  );

  const withDeletedDateCell = useMemo(() => hasDeletedConditions(conditions), [conditions]);

  const tableConfig = useMemo(
    () =>
      getConditionsTableConfig({
        isBrokerCompany: isBrokerOrSubCompanyUser,
        withDeletedDateCell,
        isManageAllowed,
        isEditAllowed,
        isSignOffAllowed,
        isUnsignOffAllowed,
        isRejectAllowed: isRejectAllowed || isOwnRejectAllowed,
        isLoanCanceled,
        loanId,
        expandedRowId,
        categoryOptions: filterOptions.categoryOptions,
        statusOptions: filterOptions.statusOptions,
        onToggleExpand,
      }),
    [
      isBrokerOrSubCompanyUser,
      withDeletedDateCell,
      isManageAllowed,
      isEditAllowed,
      isSignOffAllowed,
      isUnsignOffAllowed,
      isRejectAllowed,
      isOwnRejectAllowed,
      isLoanCanceled,
      loanId,
      expandedRowId,
      filterOptions,
      onToggleExpand,
    ],
  );

  const rows = useMemo(
    () => orderConditions({ rows: applyFilters(preparedRows, tableConfig), order }),
    [applyFilters, preparedRows, tableConfig, order],
  );

  const expandedCondition = useMemo(
    () => conditions.find(condition => condition.id === expandedRowId),
    [conditions, expandedRowId],
  );

  const expandedRowContent = useMemo(() => {
    if (!expandedCondition) {
      return null;
    }

    const relatedTasks = getRelatedTasks({
      condition: expandedCondition,
      tasks,
      isBrokerCompany: isBrokerOrSubCompanyUser,
    });

    if (!relatedTasks.length) {
      return null;
    }

    const canRemoveTasks =
      !isBrokerOrSubCompanyUser &&
      isManageAllowed &&
      !isLoanCanceled &&
      !expandedCondition.isDeleted &&
      !expandedCondition.signedOff;

    return (
      <ConditionTasksTable
        canRemoveTasks={canRemoveTasks}
        conditionId={expandedCondition.id}
        entityIndex={entityIndex}
        isBrokerCompany={isBrokerOrSubCompanyUser}
        taskTypesMap={taskTypesMap}
        tasks={relatedTasks}
      />
    );
  }, [
    expandedCondition,
    tasks,
    taskTypesMap,
    entityIndex,
    isBrokerOrSubCompanyUser,
    isManageAllowed,
    isLoanCanceled,
  ]);

  const actionContent = !isLoanCanceled && (
    <Button
      data-testid={ConditionsTestIds.ADD_BUTTON}
      disabled={!isManageAllowed || isGetConditionsPending}
      startIcon={<AddBoxIcon />}
      onClick={onAddConditions}
    >
      Add Condition
    </Button>
  );

  return (
    <PageBlock
      withAction
      withDivider
      actionContent={actionContent}
      data-testid={ConditionsTestIds.SECTION}
      title="Conditions"
    >
      <TabsCDS
        allowScrollButtonsMobile={isMobile}
        data-testid={ConditionsTestIds.TABS}
        tabs={tabs}
        value={activeTabValue}
        variant={isMobile ? 'scrollable' : 'standard'}
        onChange={onTabChange}
      />
      <SystemTable
        additionalRowContent={expandedRowContent}
        additionalRowContentRowId={expandedRowId}
        columnFilters={columnFilters}
        config={tableConfig}
        customStyles={conditionsTableStyles}
        data={rows}
        isLoading={isGetConditionsPending}
        order={order}
        skeletonRows={5}
        testId={ConditionsTestIds.TABLE}
        onColumnFiltersChange={onColumnFiltersChange}
        onRequestSort={onRequestSort}
      />
      {!isBrokerOrSubCompanyUser && loanId && (
        <AddConditionsForm
          customOnAssignFormClick={customOnAssignFormClick}
          initialForm={addModalState.initialForm}
          isAdhocAllowed={isAdhocCreateAllowed}
          isOpen={addModalState.isOpen}
          key={addModalState.key}
          loanId={loanId}
          onClose={onCloseAddModal}
        />
      )}
    </PageBlock>
  );
};

Conditions.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Conditions);
